import { map, Observable } from 'rxjs';
import {
  BatchDomain,
  CtBatch,
  CtBatchLock,
  CtBatchState,
  CtBatchUnion,
} from '../model/ct-batch-model';
import { Injectable } from '@angular/core';
import { ApiService } from '@ps-gui/shared-core';

export type CtBatchSearchResult = Pick<CtBatchUnion, 'id' | 'auditId' | 'tenant'>;

export interface CtBatchLockCheck {
  stillHeld: boolean;
  /** Who holds the batch now - undefined when nobody does */
  lock?: CtBatchLock;
}

@Injectable({
  providedIn: 'root',
})
export class CtBatchApiService extends ApiService {
  private readonly batchIdsUnlockedByThisApp = new Set<string>();

  protected override get baseUrl(): string {
    return this.backendUrls.ctBackendUrl;
  }

  protected override get baseUrlSuffix(): string {
    return '/api/batch';
  }

  getCtBatchDomain(ctBatchId: string): Observable<BatchDomain> {
    return super.get<BatchDomain>(`${ctBatchId}/domain`);
  }

  getCtBatch(ctBatchId: string): Observable<CtBatch> {
    return super.get<CtBatch>(`search/${ctBatchId}`);
  }

  searchCtBatchesByState(state: CtBatchState): Observable<CtBatchSearchResult[]> {
    return super.get<CtBatchSearchResult[]>(`search/audit?state=${state}`);
  }

  searchCtBatchesByAuditId(auditId: string): Observable<CtBatchSearchResult[]> {
    return (
      super
        .post<Record<string, CtBatchSearchResult[]>, { auditIds: string[] }>(`search/audit`, {
          auditIds: [auditId],
        })
        // The input returns a Record<auditId, SearchResult[]> but we only request one audit id
        .pipe(map((result) => Object.values(result)[0]))
    );
  }

  getCtBatchLock(ctBatchId: string): Observable<CtBatchLock | undefined> {
    return super.get<CtBatchLock | undefined>(`lock/${ctBatchId}`);
  }

  /**
   * A missing lock counts as lost, not as free. Nobody but the CT app locks the batch it is showing,
   * so the lock only disappears because someone released it elsewhere - another user taking over,
   * or this user opening a different case from the dashboard and confirming the handover. Treating
   * it as "still mine" is what used to let two cases share one browser storage.
   *
   * The unlocks this app performs itself (header back, completed case) are exempt.
   */
  checkLockHeldBy(ctBatchId: string, userId: string | undefined): Observable<CtBatchLockCheck> {
    return this.getCtBatchLock(ctBatchId).pipe(
      map((lock) => ({
        stillHeld: lock ? lock.userId === userId : this.batchIdsUnlockedByThisApp.has(ctBatchId),
        lock,
      })),
    );
  }

  lockCtBatch(ctBatchId: string): Observable<void> {
    return super.post(`lock/${ctBatchId}`);
  }

  unlockCtBatch(batchId: string): Observable<unknown> {
    this.batchIdsUnlockedByThisApp.add(batchId);

    return super.delete(`lock/${batchId}`);
  }
}
